// CompetitionCard.js - Versión Final Moderna
// src/components/gestionEvento/CompetitionCard.js

import React from 'react';
import PropTypes from 'prop-types';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  IconButton, 
  Link, 
  Chip,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import { 
  Event as EventIcon, 
  Group as GroupIcon, 
  Settings as SettingsIcon, 
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Launch as LaunchIcon,
  MonetizationOn as MoneyIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import formatCurrency from '../../utils/formatCurrency';
import CompetitionCoverImage from '../Common/CompetitionCoverImage';

const CompetitionCard = ({ 
  competencia,
  hideStatusChip = false,
  hideEnrolledCount = false,
  hideSettings = false,
}) => {
  const navigate = useNavigate();

  // ==================== UTILITY FUNCTIONS ====================
  
  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      console.warn("Error formateando fecha:", date, e);
      return '';
    }
  };

  const getDaysUntil = (date) => {
    if (!date) return null;
    const targetDate = new Date(date);
    const today = new Date();
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusConfig = (status) => {
    const configs = {
      publicado: { color: 'success', text: 'Activo', icon: '🟢' },
      pendiente: { color: 'warning', text: 'Borrador', icon: '🟡' },
      cancelado: { color: 'error', text: 'Cancelado', icon: '🔴' },
      finalizado: { color: 'default', text: 'Finalizado', icon: '⚫' }
    };
    return configs[status] || configs.pendiente;
  };

  const getTypeConfig = (type) => {
    const configs = {
      ruta: { icon: '🏃', label: 'Ruta' },
      pista_y_campo: { icon: '🏃‍♀️', label: 'Pista y Campo' },
      campo_traviesa: { icon: '🌲', label: 'Campo Traviesa' },
      otros: { icon: '🏆', label: 'Otros' }
    };
    return configs[type] || configs.otros;
  };

  // ==================== DATA EXTRACTION ====================
  
  const {
    general: {
      nombre = 'Sin Nombre',
      fechaInicioCompetencia,
      fechaFinCompetencia,
      fechaFinInscripciones,
      imagenPortada: imagen,
      precioInscripciones: precioNumerico = 0,
      estado = 'pendiente',
      tipoCompetencia = ''
    } = {},
    location: {
      lugar = 'Lugar no especificado',
      mapShortLink
    } = {},
    inscritos = 0,
    slug = '',
    id: competitionId
  } = competencia || {};

  const isGratis = precioNumerico === 0;
  const formattedPrice = formatCurrency(precioNumerico);
  const daysUntilStart = getDaysUntil(fechaInicioCompetencia);
  const daysUntilRegistrationEnd = getDaysUntil(fechaFinInscripciones);
  const statusConfig = getStatusConfig(estado);
  const typeConfig = getTypeConfig(tipoCompetencia);

  // ==================== EVENT HANDLERS ====================
  
  const handleNavigateToView = () => {
    if (slug) {
      navigate(`/${slug}`);
    } else {
      console.error("Slug no disponible para navegar");
    }
  };

  const handleNavigateToSettings = () => {
    if (competitionId) {
      navigate(`/${competitionId}/ajustes`);
    } else {
      console.error("ID de competencia no disponible");
    }
  };

  // ==================== RENDER HELPERS ====================
  
  const renderInfoRow = (icon, bgcolor, content, subContent = null) => (
    <Box display="flex" alignItems="center" mb={1.5}>
      <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor }}>
        {icon}
      </Avatar>
      <Box flex={1}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {content}
        </Typography>
        {subContent && (
          <Typography 
            variant="caption" 
            color="primary.main" 
            fontWeight={600}
            sx={{ display: 'block' }}
          >
            {subContent}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderUrgencyIndicator = () => {
    if (daysUntilRegistrationEnd > 0 && daysUntilRegistrationEnd <= 7) {
      return (
        <Typography 
          variant="caption" 
          color="warning.main" 
          fontWeight={600}
          sx={{ 
            display: 'block',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 }
            }
          }}
        >
          ¡Quedan {daysUntilRegistrationEnd} días!
        </Typography>
      );
    }
    return null;
  };

  const renderStatItem = (icon, bgcolor, number, label) => (
    <Box display="flex" alignItems="center">
      <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor }}>
        {icon}
      </Avatar>
      <Box>
        <Typography variant="h6" fontWeight="700" color="text.primary">
          {number}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Box>
  );

  const renderPriceSection = () => {
    if (isGratis) {
      return (
        <Chip 
          label="GRATIS" 
          color="success" 
          variant="filled"
          sx={{ 
            fontWeight: 700,
            fontSize: '0.8rem',
            px: 1,
            background: 'linear-gradient(45deg, #4caf50 30%, #8bc34a 90%)',
          }}
        />
      );
    }

    return (
      <Box display="flex" alignItems="center">
        <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor: 'success.main' }}>
          <MoneyIcon sx={{ fontSize: '0.9rem' }} />
        </Avatar>
        <Box textAlign="right">
          <Typography variant="h6" fontWeight="700" color="success.main">
            {formattedPrice}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Inscripción
          </Typography>
        </Box>
      </Box>
    );
  };

  // ==================== MAIN RENDER ====================
  
  return (
    <Card
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12)',
          borderColor: 'primary.main',
        },
      }}
    >
      {/* ==================== HEADER SECTION ==================== */}
      <Box sx={{ position: 'relative', aspectRatio: '16 / 9', width: '100%' }}>
        <CompetitionCoverImage
          src={imagen}
          alt={nombre}
          height={'100%'}
          competitionName={nombre}
          borderRadius="0"
        />
        
        {/* Status Badge */}
        {!hideStatusChip && (
          <Chip
            label={estado === 'publicado' ? 'Activo' : estado === 'pendiente' ? 'Borrador' : statusConfig.text}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              bgcolor: estado === 'publicado' ? 'success.main' : estado === 'pendiente' ? 'error.main' : 'grey.800',
              color: 'common.white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          />
        )}

        {/* Type Badge */}
        {tipoCompetencia && (
          <Chip
            icon={<TrophyIcon sx={{ fontSize: '14px !important' }} />}
            label={typeConfig.label}
            size="small"
            variant="outlined"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              fontWeight: 500,
              fontSize: '0.7rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          />
        )}
      </Box>

      {/* ==================== CONTENT SECTION ==================== */}
      <CardContent sx={{ padding: 3, flexGrow: 1 }}>
        {/* Title */}
        <Typography 
          gutterBottom 
          variant="h5" 
          fontWeight="700"
          sx={{ 
            fontSize: '1.3rem',
            lineHeight: 1.3,
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            color: 'text.primary'
          }}
        >
          {nombre}
        </Typography>

        {/* Information Rows */}
        <Box sx={{ mb: 2 }}>
          {/* Location */}
          {lugar && lugar !== 'Lugar no especificado' && (
            <Box display="flex" alignItems="center" mb={1.5}>
              <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'primary.main' }}>
                <LocationOnIcon sx={{ fontSize: '1.1rem' }} />
              </Avatar>
              {mapShortLink ? (
                <Link 
                  href={mapShortLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  underline="hover" 
                  color="inherit"
                  sx={{ 
                    fontWeight: 500,
                    '&:hover': { color: 'primary.main' },
                    transition: 'color 0.2s ease'
                  }}
                >
                  {lugar}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {lugar}
                </Typography>
              )}
            </Box>
          )}

          {/* Competition Dates */}
          {fechaInicioCompetencia && fechaFinCompetencia && (
            <Box display="flex" alignItems="center" mb={1.5}>
              <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'secondary.main' }}>
                <CalendarIcon sx={{ fontSize: '1.1rem' }} />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {formatDate(fechaInicioCompetencia)} - {formatDate(fechaFinCompetencia)}
                </Typography>
                {daysUntilStart > 0 && (
                  <Typography variant="caption" color="primary.main" fontWeight={600}>
                    En {daysUntilStart} días
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Registration Deadline */}
          {fechaFinInscripciones && (
            <Box display="flex" alignItems="center" mb={1.5}>
              <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'warning.main' }}>
                <TimeIcon sx={{ fontSize: '1.1rem' }} />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Inscripciones hasta {formatDate(fechaFinInscripciones)}
                </Typography>
                {renderUrgencyIndicator()}
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Statistics */}
        <Box 
          display="flex" 
          justifyContent={hideEnrolledCount ? 'flex-end' : 'space-between'} 
          alignItems="center" 
          mb={2}
        >
          {!hideEnrolledCount && renderStatItem(
            <GroupIcon sx={{ fontSize: '0.9rem' }} />,
            'info.main',
            inscritos,
            estado === 'finalizado' ? 'Participaron' : 'Inscritos'
          )}
          {renderPriceSection()}
        </Box>
      </CardContent>

      {/* ==================== FOOTER SECTION ==================== */}
      <Box 
        sx={{ 
          p: 2, 
          pt: 0,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
        }}
      >
        <Button 
          variant="contained" 
          color="primary" 
          size="medium"
          endIcon={<LaunchIcon />}
          onClick={handleNavigateToView} 
          disabled={!slug}
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            flexGrow: 1,
            mr: 1,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
            transition: 'all 0.2s ease'
          }}
        >
          {estado === 'finalizado' ? 'Ver Resultados' : 'Ver Competencia'}
        </Button>
        
        {!hideSettings && (
          <Tooltip title="Configurar competencia" arrow>
            <IconButton 
              color="primary" 
              onClick={handleNavigateToSettings} 
              disabled={!competitionId}
              sx={{ 
                bgcolor: 'grey.100',
                '&:hover': { 
                  bgcolor: 'primary.main',
                  color: 'white',
                  transform: 'rotate(15deg)'
                },
                '&:disabled': {
                  bgcolor: 'grey.50',
                  color: 'grey.300',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Card>
  );
};

// ==================== PROP TYPES ====================

CompetitionCard.propTypes = {
  competencia: PropTypes.shape({
    id: PropTypes.string.isRequired,
    slug: PropTypes.string,
    inscritos: PropTypes.number,
    general: PropTypes.shape({
      nombre: PropTypes.string,
      fechaInicioCompetencia: PropTypes.string,
      fechaFinCompetencia: PropTypes.string,
      fechaFinInscripciones: PropTypes.string,
      imagenPortada: PropTypes.string,
      precioInscripciones: PropTypes.number,
      estado: PropTypes.oneOf(['publicado', 'pendiente', 'cancelado', 'finalizado']),
      tipoCompetencia: PropTypes.oneOf(['ruta', 'pista_y_campo', 'campo_traviesa', 'otros']),
    }),
    location: PropTypes.shape({
      lugar: PropTypes.string,
      mapShortLink: PropTypes.string,
    }),
  }).isRequired,
  hideStatusChip: PropTypes.bool,
  hideEnrolledCount: PropTypes.bool,
  hideSettings: PropTypes.bool,
};

export default CompetitionCard;
